"use client";

/**
 * Client hook that POSTs a reply-assist request to an app-supplied endpoint and
 * returns the generated text. The endpoint owns auth + the LLM call; this hook
 * is just transport + loading/error state, so the same toolbar works in any app
 * by pointing at that app's own route.
 */

import { useCallback, useState } from "react";
import type { AiReplyIntent, AiReplyPromptInput, AiReplyResponse } from "./types";

export interface UseAiReplyAssistOptions {
  /** Defaults to "/api/ai/reply-assist". */
  endpoint?: string;
  /** Extra headers (e.g. a bearer token). Hub relies on its session cookie. */
  getAuthHeaders?: () => Promise<Record<string, string>> | Record<string, string>;
  /** `credentials` for the fetch. Defaults to "include" (cookie-authed apps). */
  credentials?: RequestCredentials;
}

export interface UseAiReplyAssistResult {
  /** Run an intent; resolves to the generated text, or null on failure. */
  run: (
    intent: AiReplyIntent,
    input: AiReplyPromptInput,
  ) => Promise<string | null>;
  isLoading: boolean;
  error: string | null;
  /** Clear the current error. */
  reset: () => void;
}

const DEFAULT_ENDPOINT = "/api/ai/reply-assist";

export function useAiReplyAssist(
  options: UseAiReplyAssistOptions = {},
): UseAiReplyAssistResult {
  const { endpoint = DEFAULT_ENDPOINT, getAuthHeaders, credentials = "include" } = options;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback<UseAiReplyAssistResult["run"]>(
    async (intent, input) => {
      setIsLoading(true);
      setError(null);
      try {
        const extraHeaders = getAuthHeaders ? await getAuthHeaders() : {};
        const res = await fetch(endpoint, {
          method: "POST",
          credentials,
          headers: { "Content-Type": "application/json", ...extraHeaders },
          body: JSON.stringify({ intent, ...input }),
        });
        if (!res.ok) {
          let message = `Request failed (${res.status})`;
          try {
            const body = (await res.json()) as { error?: string };
            if (body?.error) message = body.error;
          } catch {
            // non-JSON error body — keep the status message
          }
          throw new Error(message);
        }
        const data = (await res.json()) as AiReplyResponse;
        const text = typeof data?.text === "string" ? data.text.trim() : "";
        if (!text) throw new Error("The assistant returned an empty response.");
        return text;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Something went wrong.");
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [endpoint, getAuthHeaders, credentials],
  );

  const reset = useCallback(() => setError(null), []);

  return { run, isLoading, error, reset };
}
